import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { Dimension, ExtendedValence, Valence, ValueChain } from '../../types';
import { PredictionByClassifier } from '../article-predictions/types';
import { ILinkChainWithWeights } from '../link-chains/types';
import { z } from 'zod';

export enum ImpactScoreType {
  // organic
  WIKI_BCA = 'wiki-bca',
  CORE_BCA = 'core-bca',
  CORE_BSF = 'core-bsf',
  WIKI_BSF = 'wiki-bsf',
  DIS = 'dis',
  SDBS = 'sdbs',
  PIAAC = 'piaac',
  MERGED_OS = 'merged-os',
  NUTRI = 'nutri',

  // flattened scores
  WIKI_BCA_FS = 'wiki-bca-fs',
  CORE_BCA_FS = 'core-bca-fs',
  CORE_BSF_FS = 'core-bsf-fs',
  WIKI_BSF_FS = 'wiki-bsf-fs',
  DIS_FS = 'dis-fs',
  MERGED_FS = 'merged-fs',
  CONSTRAINED_FS = 'constrained-fs',

  FULL = 'full',
  NORMALIZED = 'normalized',
  FINAL = 'final', // constrained

  // test
  TEST = 'test',
}

export enum FinalImpactScoreType {
  ABSOLUTE = 'a',
  RELATIVE = 'r',
  CENTS_PER_DOLLAR = 'cents-per-dollar',
  DOLLARS_PER_YEAR = 'dollars-per-year',
}

export interface ByImpactScoreType<T> {
  [FinalImpactScoreType.ABSOLUTE]: T;
  [FinalImpactScoreType.RELATIVE]: T;
}

export enum ScoreBreakdownRelationType {
  DOWNSTREAM = 'D',
  UPSTREAM = 'U',
  OWN = 'O',
  CHILD = 'C',
  PARENT = 'P',
  ENABLES = 'E',
  ENABLED_BY = 'B',
  OTHER = 'OT',
}

export enum NetScoreFormat {
  NET_RATIO = 'NET_RATIO',
  NET_RATIO_DECIMAL = 'NET_RATIO_DECIMAL',
  NET_SUM = 'NET_SUM',
}

export const DEFAULT_RELATIVE_NET_SCORE_FORMAT = NetScoreFormat.NET_RATIO;

/**
 * Impact Scores grouped by
 * (V)alue chain and (V)alence
 */

export interface ImpactScoresByVV {
  [valueChain: string]: ImpactScoresByV;
}
/**
 * Impact Scores grouped by (V)alence
 */

export interface ImpactScoresByV {
  [valence: string]: ImpactScoreDatum;
}

export interface ProductImpactScoresByV {
  [valence: string]: ImpactScoreDatumForProduct;
}
// Note: doesn't have Valence.OTHER

export type ImpactScoresByDV = {
  [d in Dimension]: {
    [Valence.POSITIVE]: number;
    [Valence.NEGATIVE]: number;
  };
};
/**
 * Impact Scores grouped by
 * P(product) code, (D)imension and (V)alence
 */

export interface ImpactScoresByPDV {
  [productCode: string]: ImpactScoresByDV;
}
/**
 * Impact Scores grouped by
 * P(product) code, (I)mpact category code, (V)alue chain and (V)alence
 *
 * We use such a deep grouping because that
 * makes it efficient to merge such objects
 */

export interface ImpactScoresByPIVV {
  [productCode: string]: ImpactScoresByIVV;
}
/**
 * Impact Scores grouped by
 * P(product) code, (I)mpact category code (V)alence
 *
 * Summed over value chains. Note that
 * makes it efficient to merge such objects
 */

export interface ProductImpactScoresByPIV {
  [productCode: string]: {
    [impactCategoryCode: string]: ProductImpactScoresByV;
  };
}
/**
 * Impact Scores grouped by
 * (I)mpact category code, (V)alue chain and (V)alence
 */

export interface ImpactScoresByIVV {
  [impactCategoryCode: string]: ImpactScoresByVV;
}
/**
 * Impact Scores grouped by
 * (Type), (I)mpact category code, (V)alue chain and (V)alence
 */

export interface ImpactScoresByTIVV {
  [FinalImpactScoreType.RELATIVE]: ImpactScoresByIVV;
  [FinalImpactScoreType.ABSOLUTE]?: ImpactScoresByIVV;
}

export type ByImpactValence<T> = { [impact in ImpactCategoryId]: { [valence in Valence]: T } };

/**
 * Impact score, i.e. a score that is associated to an impact
 */

export interface ImpactScoreDatum {
  impactCode: ImpactCategoryId;
  value: number;
  valence: Valence;
  valueChain: ValueChain;
}

export interface ImpactScoreDatumForProduct extends ImpactScoreDatum {
  productCode: string;
}

/**
 * Runtime shape of the impact score payloads, for validating them where they arrive from the
 * API. Only the field the score readers actually take numbers out of is asserted to be a
 * number: `pickScore` reads the leaf with `lodash.get(…, [impactCode, valueChain, valence,
 * 'value'], 0)`, so a leaf that lost or renamed `value` reads as a hard `0` and renders as a
 * real, wrong score rather than as an error.
 *
 * The code-like keys are validated as strings rather than as their enums, because a backend
 * that starts emitting a new impact category or value chain must not fail the whole response.
 */
const zImpactScoreDatum = z.object({
  impactCode: z.string(),
  value: z.number(),
  valence: z.string(),
  valueChain: z.string(),
});

export const zImpactScoresByIVV = z.record(z.record(z.record(zImpactScoreDatum)));

/**
 * Same shape as {@link zImpactScoresByIVV}, but tolerant of a `null` leaf `value`: a
 * malformed/undividable scale factor upstream (e.g. a missing market-size total for an
 * in-development product-score version, PLT-6425) can leave every ABSOLUTE leaf `null`
 * on the wire. Failing the whole entity response over that would also take down the
 * (unaffected, valid) RELATIVE scores it arrives alongside, so a `null` here is coalesced
 * to `0` rather than rejected — consistent with how a missing leaf already reads as `0`
 * (see `zImpactScoreDatum`'s doc comment above). RELATIVE keeps the strict schema: it is
 * the score every lens is built on, so corruption there should still fail loudly.
 */
const zImpactScoresByIVVLenient = z.record(
  z.record(
    z.record(
      zImpactScoreDatum.extend({
        value: z
          .number()
          .nullable()
          .transform(value => value ?? 0),
      }),
    ),
  ),
);

export const zImpactScoresByTIVV = z.object({
  // Both are optional on purpose: entities the viewer may not read output for come back with
  // the score maps emptied out (`{}` or `{ r: {} }`) rather than removed.
  [FinalImpactScoreType.RELATIVE]: zImpactScoresByIVV.optional(),
  [FinalImpactScoreType.ABSOLUTE]: zImpactScoresByIVVLenient.optional(),
});

/**
 * Lean representation for ImpactScoresByIVV
 */

export interface PackedImpactScoresByIVV {
  [impactCategoryCode: string]: {
    [valueChain: string]: {
      [valence: string]: number;
    };
  };
}

export interface LinkChainImpactScoreContribution {
  chain: ILinkChainWithWeights;
  // score for product at end of the link chain
  // (with no weight applied yet)
  scores: PackedImpactScoresByIVV;
}

export type NetScoresByType = {
  [key in FinalImpactScoreType]?: number | undefined;
};

/**
 * The headline net impact numbers, as they arrive from the API. Every score is optional (which
 * ones are present depends on the entity type and on the viewer's access), so what this asserts
 * is that a score that *is* present is a number: these values are formatted straight into the
 * net impact ratio, where a string would be shown as a plausible-looking figure.
 *
 * `null` is accepted even though `NetScoresByType` above does not admit it, because groups and
 * portfolios send it: `weight_set_result.absolute` is `@AllowNull(true)` and the groupfolio
 * fetcher spreads the row through without the `mapNullValuesToUndefined` the company path
 * applies, so `{"a":null,"r":0.42}` is a real payload. The NULL is written whenever the scale
 * factor is undefined — always for `MeasurableGroupType.INDEX` and `UNWEIGHTED_PORTFOLIO`, and
 * for funds, weighted portfolios, private/loans/grants/clients groups whose total value in USD
 * is unknown (`aggregate-weight-set-scores`). Every reader already coalesces a missing score, so
 * the null and the absent key mean the same thing to the UI. Reconciling type and wire is
 * PLT-6185; until then this is one of the places `SchemaAccepts` cannot police, since it only
 * proves the schema accepts what the *declared* type allows.
 */
export const zNetScoresByType = z.object({
  [FinalImpactScoreType.ABSOLUTE]: z.number().nullish(),
  [FinalImpactScoreType.RELATIVE]: z.number().nullish(),
  [FinalImpactScoreType.CENTS_PER_DOLLAR]: z.number().nullish(),
  [FinalImpactScoreType.DOLLARS_PER_YEAR]: z.number().nullish(),
});

/**
 * Score data (matchcount and predictions)
 * for a single impactTerm/productTerm pair
 */
export interface TermPairScorePrimitives {
  impactTerm: string;
  productTerm: string;
  matchCount: number;
  predictions?: PredictionByClassifier;
}

/**
 * Score for a productPhrase/impactPhrase pair (term pair score)
 */
export interface TermPairScoreDatum {
  impactCode: string;
  productTerm: string;
  impactTerm: string;
  valence: Valence;
  extendedValence: ExtendedValence;
  value: number;
  // the score primitives from which
  // the score was calculated
  scorePrimitives?: TermPairScorePrimitives;
  valueChain: ValueChain;
  appliedFilter?: string;
  articleCount?: number;
  source?: number;
}

export interface FullImpactScoresForProduct {
  own: PackedImpactScoresByIVV;
  aggregate: PackedImpactScoresByIVV;
  contributions: LinkChainImpactScoreContribution[];
}
/**
 * Identifier for the article classification
 * (prediction) method
 */
/**
 * Parameters that define how impact scores are calculated
 * TODO: remove?
 */
export interface ScoringParams {
  predictionMethod: PredictionMethod;
  method: TermPairScoringMethod;
  minimumCertainty: number;
  minimumMatchCount: number;
}

/**
 * Method for calculating term pair scores
 */
export enum TermPairScoringMethod {
  MATCH_COUNT = 'match-count',
  UNWEIGHTED_PROBABILITIES = 'unweighted-probabilities',
  MATCH_COUNT_WEIGHTED_PROBABILITIES = 'match-count-weighted-probabilities',
  LOG_MATCH_COUNT_WEIGHTED_PROBABILITIES = 'log-match-count-weighted-probabilities',
}

export enum PredictionMethod {
  AZALEA = 'azalea',
}

export const nonExistingValencesForCategory = ['H3N', 'K1P', 'K2N', 'K3N', 'S1N', 'S2N', 'S3N'] as const;
export type NonExistingValencesForCategory = (typeof nonExistingValencesForCategory)[number];

export function isImpactScoreIVKey(key: `${ImpactCategoryId}${Valence}`): key is ImpactScoreIVKey {
  return !nonExistingValencesForCategory.includes(key as NonExistingValencesForCategory);
}

export function isValidValenceForImpactCategory(impactCode: ImpactCategoryId, valence: Valence) {
  const impactScoreIVKey = toImpactScoreIVKey({ impactCode, valence });
  return !!impactScoreIVKey && isImpactScoreIVKey(impactScoreIVKey);
}

export function toImpactScoreIVKey(datum: { valence: Valence; impactCode: ImpactCategoryId }) {
  const { valence, impactCode } = datum;
  const key = `${impactCode}${valence}` as const;
  return isImpactScoreIVKey(key) ? key : undefined;
}

export function fromImpactScoreIVKey(key: ImpactScoreIVKey) {
  const impactCode = key.slice(0, 2) as ImpactCategoryId;
  const valence = key.at(2) as Valence;
  return { impactCode, valence };
}

export type ImpactScoreIVKey = Exclude<
  `${ImpactCategoryId}${Valence.POSITIVE | Valence.NEGATIVE}`,
  NonExistingValencesForCategory
>;

export type ImpactScoreAttributesByIV = {
  [key in ImpactScoreIVKey]: number | null;
};

export const impactScoreKeys: ImpactScoreIVKey[] = Object.values(ImpactCategoryId).flatMap(s => {
  const positiveKey = `${s}${Valence.POSITIVE}` as const;
  const negativeKey = `${s}${Valence.NEGATIVE}` as const;
  return [positiveKey, negativeKey].filter(isImpactScoreIVKey);
});

export function isImpactScoreKey(input: string): input is ImpactScoreIVKey {
  return impactScoreKeys.includes(input as ImpactScoreIVKey);
}
export type NetScoreColumnId = 'netImpactSum';

export const netImpactSumPaths: NetScoreColumnId[] = ['netImpactSum'];

export const zImpactScoreIVKey = z
  .string()
  .pipe(z.custom<ImpactScoreIVKey>((input: string) => isImpactScoreKey(input)));
